import { LinkType, LinkTypeDisplay } from 'src/graphql/types/trackingLinks'

import { formatSnakeValue } from './formatters'

/**
 * Sentinel used as the Lead Source filter value for tracking links that have
 * no custom name. Must stay in sync with LEAD_SOURCE_NO_NAME in
 * api/src/services/applicants/applicantsFilters.ts
 */
export const LEAD_SOURCE_NO_NAME = '__NO_NAME__'

const NO_NAME_LABEL = '(No name)'

type TrackingLinkLike = {
  linkType?: LinkType | string | null
  linkName?: string | null
} | null

/**
 * Splits a tracking link into its display parts: the human-readable type
 * (e.g. "Recruiter") and the customizable name (e.g. "Allison"). Either may be
 * null. Lets callers render the two pieces however they need — e.g. the
 * Applicants table stacks them on two lines, while the Create Applicant drawer
 * joins them into one label.
 */
export const getLeadSourceParts = (
  trackingLink?: TrackingLinkLike
): { typeLabel: string | null; linkName: string | null } => {
  const linkType = trackingLink?.linkType
  const typeLabel = linkType
    ? LinkTypeDisplay[linkType as LinkType] || formatSnakeValue(linkType)
    : null
  const linkName = trackingLink?.linkName?.trim() || null

  return { typeLabel, linkName }
}

/**
 * Single-line lead-source label combining the type with its customizable name
 * (e.g. "Recruiter: Allison"); falls back to just the type, or `null` when
 * there's no tracking link / type.
 */
export const formatLeadSourceLabel = (
  trackingLink?: TrackingLinkLike
): string | null => {
  const { typeLabel, linkName } = getLeadSourceParts(trackingLink)
  if (!typeLabel) return null

  return linkName ? `${typeLabel}: ${linkName}` : typeLabel
}

/**
 * Formats an encoded Lead Source *filter* value into a readable chip label.
 * Values are either a bare LinkType ("RECRUITER" → "Recruiter") or a
 * "TYPE:name" pair ("RECRUITER:Allison" → "Recruiter: Allison"); the
 * "TYPE:__NO_NAME__" sentinel renders as "Recruiter: (No name)". Splits on the
 * first colon so names containing colons are preserved. Falls back to the raw
 * value if the type can't be resolved.
 */
export const formatLeadSourceFilterValue = (value: string): string => {
  const separatorIndex = value.indexOf(':')
  if (separatorIndex === -1) {
    return formatLeadSourceLabel({ linkType: value }) || value
  }

  const linkType = value.slice(0, separatorIndex)
  const namePart = value.slice(separatorIndex + 1)
  const linkName = namePart === LEAD_SOURCE_NO_NAME ? NO_NAME_LABEL : namePart

  return formatLeadSourceLabel({ linkType, linkName }) || value
}

/**
 * Builds the encoded lead-source *filter* value for a link type, optionally
 * scoped to one of its names — the inverse of formatLeadSourceFilterValue.
 * Omitting `linkName` yields the bare type ("RECRUITER" = the whole type); a
 * name yields "RECRUITER:Allison", and an explicitly nameless link yields the
 * "RECRUITER:__NO_NAME__" sentinel.
 */
export const encodeLeadSourceValue = (
  linkType: string,
  linkName?: string | null
): string => {
  if (linkName === undefined) return linkType
  return `${linkType}:${linkName ?? LEAD_SOURCE_NO_NAME}`
}

/** A distinct (type, name) pair as returned by the companyLeadSources query. */
export interface CompanyLeadSource {
  linkType: LinkType | string
  linkName: string | null
}

/** A nested filter option: a link type parent with its link-name children. */
export interface LeadSourceOption {
  value: string
  label: string
  options: { value: string; label: string }[]
}

/**
 * Groups the distinct company lead sources into a nested option tree — each
 * in-use link type is a parent, its distinct custom names are the children
 * (with LEAD_SOURCE_NO_NAME / "(No name)" for links without a name). Only types
 * the company actually uses appear, rather than the full LinkType enum. Shared
 * by the Applicants and Pipeline Analytics lead-source filters.
 */
export const buildLeadSourceOptions = (
  sources: CompanyLeadSource[]
): LeadSourceOption[] => {
  const childrenByType = new Map<string, { value: string; label: string }[]>()

  for (const { linkType, linkName } of sources) {
    const children = childrenByType.get(linkType) || []
    children.push({
      value: linkName ?? LEAD_SOURCE_NO_NAME,
      label: linkName ?? NO_NAME_LABEL,
    })
    childrenByType.set(linkType, children)
  }

  return Array.from(childrenByType.entries()).map(([linkType, children]) => ({
    value: linkType,
    label: LinkTypeDisplay[linkType as LinkType] || formatSnakeValue(linkType),
    options: children,
  }))
}
